import { firebaseApp, firestore, firebaseAuth } from 'app';
import {

  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  doc,
  serverTimestamp,
  getDocs,
  getDoc,
  deleteDoc,
  Timestamp,
  writeBatch,
  arrayUnion,
  increment
} from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { Conversation, ConversationCreateData, Message, ChatUser, ChatType } from './chatTypes';
import brain from 'brain';
import { useStaffStore } from 'utils/useStaffStore';
import { useClientStore } from 'utils/useClientStore';

// Debug logger — silent in production, verbose only when explicitly
// enabled by setting `window.__CHAT_DEBUG__ = true` from the console.
// The previous `console.log` calls flooded the chat page on prod which
// hurt performance and made real errors harder to spot.
const dlog = (...args: unknown[]) => {
  if (typeof window !== 'undefined' && (window as any).__CHAT_DEBUG__) {
    // eslint-disable-next-line no-console
    console.log('[Chat Service]', ...args);
  }
};

const db = firestore;
const storage = getStorage(firebaseApp);

// Remove the old cache and pending requests as they're now handled by the store
// const userDetailsCache = new Map<string, { name: string; photoURL?: string; timestamp: number }>();
// const pendingRequests = new Map<string, Promise<{ name: string; photoURL?: string }>>();
// const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Helper function to convert Firestore timestamp to Date
const convertTimestamp = (timestamp: any): Date => {
  try {
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate();
    }
    if (timestamp?.seconds) {
      return new Date(timestamp.seconds * 1000);
    }
    if (timestamp) {
      const date = new Date(timestamp);
      // Check if valid date
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
    return new Date(); // Fallback to current date
  } catch {
    return new Date(); // Fallback to current date on any error
  }
};

// Track conversation user details cache to avoid re-fetching for unchanged conversations
const conversationUserCache = new Map<string, { otherUserName?: string; otherUserAvatar?: string; }>();

// ── Mobile-schema participant-detail resolution ─────────────────────
// Native (React Native build 2.6.0+) reads `participantNames`,
// `participantPhotos`, and `participantRoles` as the canonical maps
// keyed by uid. Web previously wrote `participantDetails` only — so
// every conv created from the web showed up as "Unknown User" on
// mobile. This helper resolves all participants in parallel (via the
// staff/client store caches, falling back to a single network fetch
// per uid) and returns the three maps so every conversation-create
// write site can include them.
const resolveParticipantMaps = async (
  uids: string[],
  seed: { uid: string; name: string; photoURL?: string | null; role?: string } | null,
): Promise<{
  participantNames: Record<string, string>;
  participantPhotos: Record<string, string>;
  participantRoles: Record<string, string>;
}> => {
  const names: Record<string, string> = {};
  const photos: Record<string, string> = {};
  const roles: Record<string, string> = {};

  // Seed creator first so we don't refetch them.
  if (seed?.uid) {
    names[seed.uid] = seed.name || 'Unknown User';
    if (seed.photoURL) photos[seed.uid] = seed.photoURL;
    if (seed.role) roles[seed.uid] = seed.role;
  }

  const staffState = useStaffStore.getState();
  const clientState = useClientStore.getState();

  const unique = Array.from(new Set(uids.filter(Boolean)));
  await Promise.all(unique.map(async (uid) => {
    if (uid === seed?.uid) return; // already seeded

    // Try synchronous cache hits first.
    const cachedStaff = staffState.getStaffUser(uid);
    if (cachedStaff) {
      names[uid] = cachedStaff.name || 'Unknown User';
      if (cachedStaff.photoURL) photos[uid] = cachedStaff.photoURL;
      if (cachedStaff.role) roles[uid] = cachedStaff.role;
      return;
    }
    const cachedClient = clientState.getClientUser(uid);
    if (cachedClient) {
      names[uid] = cachedClient.name || 'Unknown User';
      if (cachedClient.photoURL) photos[uid] = cachedClient.photoURL;
      roles[uid] = cachedClient.role || 'client';
      return;
    }

    // Fall back to network lookup (uses the same dedup logic as
    // getUserDetailsFromStore). Best-effort — never let a single
    // missing user block the whole create.
    try {
      const details = await getUserDetailsFromStore(uid);
      names[uid] = details.name || 'Unknown User';
      if (details.photoURL) photos[uid] = details.photoURL;
      // Re-check the stores — fetchStaffUser/fetchClientUser populates
      // them as a side effect.
      const refreshedStaff = useStaffStore.getState().getStaffUser(uid);
      const refreshedClient = useClientStore.getState().getClientUser(uid);
      const resolvedRole = refreshedStaff?.role || refreshedClient?.role;
      if (resolvedRole) roles[uid] = resolvedRole;
    } catch (err) {
      // Leave the entry blank — the mobile-side filter treats missing
      // names as "Unknown User" gracefully now that the FlatList
      // sender-drop fix landed.
      console.warn('[Chat] Could not resolve participant', uid, err);
      names[uid] = 'Unknown User';
    }
  }));

  return { participantNames: names, participantPhotos: photos, participantRoles: roles };
};

// Track pending requests to prevent duplicate calls for the same user
const pendingUserRequests = new Map<string, Promise<{ name: string; photoURL?: string }>>();

// Helper function to get user details using appropriate store
const getUserDetailsFromStore = async (userId: string): Promise<{ name: string; photoURL?: string }> => {
  // Check if there's already a pending request for this user
  const existingRequest = pendingUserRequests.get(userId);
  if (existingRequest) {
    return existingRequest;
  }
  
  // Try both stores to find the user
  const { getStaffUser, fetchStaffUser } = useStaffStore.getState();
  const { getClientUser, fetchClientUser } = useClientStore.getState();
  
  // Check if user is in staff cache first
  let staffUser = getStaffUser(userId);
  if (staffUser) {
    return {
      name: staffUser.name,
      photoURL: staffUser.photoURL
    };
  }
  
  // Check if user is in client cache
  let clientUser = getClientUser(userId);
  if (clientUser) {
    return {
      name: clientUser.name,
      photoURL: clientUser.photoURL
    };
  }
  
  // User not in cache, create a new request with deduplication
  const requestPromise = (async () => {
    try {
      // Try staff store first
      const staffUser = await fetchStaffUser(userId);
      return {
        name: staffUser.name,
        photoURL: staffUser.photoURL
      };
    } catch (staffError) {
      // If staff fetch fails, try client store
      try {
        const clientUser = await fetchClientUser(userId);
        return {
          name: clientUser.name,
          photoURL: clientUser.photoURL
        };
      } catch (clientError) {
        console.warn('Failed to fetch user details for', userId, 'from both stores');
        return {
          name: 'Unknown User'
        };
      }
    }
  })();
  
  // Store the promise to prevent duplicate requests
  pendingUserRequests.set(userId, requestPromise);
  
  // Clean up the pending request when it completes
  requestPromise.finally(() => {
    pendingUserRequests.delete(userId);
  });
  
  return requestPromise;
};

// Create a new conversation
export const createConversation = async (
  data: ConversationCreateData,
  currentUser: ChatUser
): Promise<string> => {
  try {
    // Ensure current user is in participants
    const participants = data.participants.includes(currentUser.id)
      ? data.participants
      : [...data.participants, currentUser.id];

    // Resolve the canonical mobile-schema maps. See
    // `resolveParticipantMaps` for the dedup / store-fallback logic.
    const { participantNames, participantPhotos, participantRoles } =
      await resolveParticipantMaps(participants, {
        uid: currentUser.id,
        name: currentUser.name,
        photoURL: currentUser.photoURL,
        role: currentUser.role,
      });

    // Merge caller-provided seeds (e.g. hidden super-admins that the
    // staff/client stores can't resolve) over any gaps.
    for (const s of data.participantSeeds || []) {
      if (!participantNames[s.id] || participantNames[s.id] === 'Unknown User') {
        participantNames[s.id] = s.name;
      }
      if (s.photoURL && !participantPhotos[s.id]) participantPhotos[s.id] = s.photoURL;
      if (s.role && !participantRoles[s.id]) participantRoles[s.id] = s.role;
    }

    // Backfill `clientName` from the resolved `participantNames` map
    // when the caller didn't pass one. Most "Start a chat with admins"
    // entry points pass `currentUser.name`, but if that resolves to
    // an email (e.g. Firebase Auth displayName unset) we'd prefer the
    // proper name we resolved via the staff/client stores. Source-of-
    // truth precedence: explicit `data.clientName` → resolved map →
    // null.
    const resolvedClientId = data.clientId || null;
    const resolvedClientName =
      data.clientName
      || (resolvedClientId ? participantNames[resolvedClientId] : null)
      || null;

    const conversationData: Record<string, unknown> = {
      type: data.type,
      participants,
      // Mobile-schema canonical maps (keyed by uid)
      participantNames,
      participantPhotos,
      participantRoles,
      clientId: resolvedClientId,
      clientName: resolvedClientName,
      createdAt: serverTimestamp(),
      createdBy: currentUser.id,
      lastMessage: null,
      lastMessageAt: serverTimestamp(),
      // Mobile reads `lastMessageTime` too — write the same value so
      // the chat list orders identically on both clients.
      lastMessageTime: serverTimestamp(),
      lastMessageBy: null,
      // Per-participant unread counter — initialised empty; send
      // paths populate it via FieldValue.increment.
      unreadCount: {},
    };

    // Native "Office Admin" direct-chat marker: exactly one staff /
    // contractor uid — the person the chat is "with".
    if (Array.isArray(data.directMembers) && data.directMembers.length > 0) {
      conversationData.directMembers = data.directMembers;
    }

    // Per native handoff rule #10: `name` is group-only. Don't write
    // a `null` name on direct/one_on_one — mobile treats absence as
    // "use participantNames" but a literal `null` field is ambiguous.
    if (data.type === 'group' || data.type === 'client_discussion') {
      conversationData.name = data.name || null;
    } else if (data.name) {
      conversationData.name = data.name;
    }

    const docRef = await addDoc(collection(db, 'conversations'), conversationData);

    // Create initial system message in subcollection. System messages
    // still need senderId + createdAt + timestamp (native filter drops
    // anything missing senderId/createdAt).
    await addDoc(collection(db, 'conversations', docRef.id, 'messages'), {
      senderId: currentUser.id,
      senderName: currentUser.name,
      senderPhotoURL: currentUser.photoURL || null,
      content: `${currentUser.name} created this conversation`,
      // Mobile reads `content || text` — write both
      text: `${currentUser.name} created this conversation`,
      createdAt: serverTimestamp(),
      // Mobile reads `createdAt || timestamp` — write both
      timestamp: serverTimestamp(),
      type: 'system',
      read: false,
    });

    return docRef.id;
  } catch (error) {
    console.error('Error creating conversation:', error);
    throw error;
  }
};

// ── Shared unread-state helpers (mirrors React Native build 2.6.0) ──
//
// BUG 1 (sender sees badge on own message): every send path must
// increment `unreadCount.{p}` ONLY for participants other than the
// sender, AND force-zero `unreadCount.{senderId}` defensively so any
// stale value from a prior bad write gets cleaned up.
//
// BUG 2 (shared staff read state for admin_client): when ANY staff
// member reads an admin_client chat, the unread badge clears for
// EVERY non-client participant. The client's own state is untouched.
//
// Both helpers return a single update object that the caller merges
// into its updateDoc / batch.update so we stay atomic. Loading the
// conversation snapshot is the caller's job — these are pure
// functions of (conversation data, current user).

/**
 * Build the `unreadCount.{uid}` deltas for a send.
 *   • CLIENT sender (admin_client) → every staff participant gets
 *     `increment(1)` — the team must see the client's message.
 *   • STAFF sender in an admin_client chat → ONLY the client gets
 *     `increment(1)`. All-one-admin-team rule: a staff reply must not
 *     re-flag the chat as unread for the rest of the staff, so their
 *     counters are force-zeroed (also clears stale badges).
 *   • Any other conversation type → every non-sender participant
 *     gets `increment(1)` (unchanged).
 *   • The sender's own counter is always forced to 0.
 * Returns `{}`-ish when participants is empty / missing.
 */
const buildUnreadIncrementsForSend = (
  convData: { participants?: string[] | null; type?: string; clientId?: string | null } | null | undefined,
  senderId: string,
): Record<string, unknown> => {
  const updates: Record<string, unknown> = {};
  const participants = convData?.participants;
  const isAdminClient = convData?.type === 'admin_client' && !!convData?.clientId;
  const staffSender = isAdminClient && senderId !== convData!.clientId;
  if (Array.isArray(participants)) {
    for (const p of participants) {
      if (!p || p === senderId) continue;
      if (staffSender && p !== convData!.clientId) {
        updates[`unreadCount.${p}`] = 0;
        continue;
      }
      updates[`unreadCount.${p}`] = increment(1);
    }
  }
  // Always force-zero the sender's own counter, even if they aren't
  // in `participants` somehow — protects against pre-fix polluted data.
  updates[`unreadCount.${senderId}`] = 0;
  return updates;
};

/**
 * Build the `unreadCount.{uid}` clears for a read.
 *   • admin_client + staff reader → clear ALL non-client participants
 *     (the rest of the team doesn't need to keep being prompted).
 *   • Anything else → clear only the reader's own counter.
 */
const buildUnreadClearsForRead = (
  convData: {
    type?: string;
    clientId?: string;
    participants?: string[];
  } | null | undefined,
  readerId: string,
): Record<string, unknown> => {
  const updates: Record<string, unknown> = {};
  const isAdminClient = convData?.type === 'admin_client';
  const isStaffReader = isAdminClient && !!convData?.clientId && readerId !== convData.clientId;
  if (isStaffReader && Array.isArray(convData?.participants)) {
    for (const p of convData!.participants!) {
      if (p && p !== convData!.clientId) {
        updates[`unreadCount.${p}`] = 0;
      }
    }
  } else {
    updates[`unreadCount.${readerId}`] = 0;
  }
  return updates;
};

/**
 * Single source of truth for "does this conversation show as unread for
 * this user". Prefers the shared per-user `unreadCount` counter (zeroed
 * for the WHOLE team when any staff member reads an admin_client chat)
 * and only falls back to the reader's own lastReadBy timestamp for
 * legacy conversations without the counter map.
 */
export const conversationHasUnread = (
  conversation: {
    lastMessageAt?: Date | null;
    lastMessageBy?: string | null;
    unreadCount?: Record<string, number> | null;
    lastReadBy?: Record<string, Date> | null;
  },
  userId: string,
): boolean => {
  if (!conversation.lastMessageAt || !conversation.lastMessageBy) return false;
  if (!userId || conversation.lastMessageBy === userId) return false;
  const counterRaw = conversation.unreadCount?.[userId];
  if (typeof counterRaw === 'number') {
    return counterRaw > 0;
  }
  const lastReadTime = conversation.lastReadBy?.[userId];
  if (!lastReadTime) return true;
  return conversation.lastMessageAt > lastReadTime;
};

// Send a message
export const sendMessage = async (
  conversationId: string,
  content: string,
  currentUser: ChatUser,
  replyTo?: { id: string; text: string; senderName: string; type?: string } | null
): Promise<void> => {
  try {
    // Write message to subcollection. Mobile (RN build 2.6.0+) reads
    // `content || text` and `createdAt || timestamp` and DROPS any
    // message missing senderId or createdAt — write both halves of
    // each dual field so iOS doesn't re-measure the FlatList.
    const messageData: any = {
      senderId: currentUser.id,
      senderName: currentUser.name,
      senderPhotoURL: currentUser.photoURL || null,
      content,
      text: content,
      createdAt: serverTimestamp(),
      timestamp: serverTimestamp(),
      type: 'text',
      read: false,
    };
    // Messenger-style reply metadata — same shape the native app writes
    // ({id, text, senderName, type}). Only present when replying; never
    // written as null/undefined.
    if (replyTo && replyTo.id) {
      messageData.replyTo = {
        id: replyTo.id,
        text: replyTo.text || '',
        senderName: replyTo.senderName || '',
        type: replyTo.type || 'text',
      };
    }

    await addDoc(collection(db, 'conversations', conversationId, 'messages'), messageData);

    // Mirror native build 2.6.0 — increment unreadCount for every
    // recipient and force-zero the sender's own counter. Reads the
    // conversation snapshot first so we know who the recipients are
    // (the conversation doc is the source of truth for `participants`,
    // not local state). One extra read per send is the price for
    // keeping mobile + web badges in sync.
    const convRef = doc(db, 'conversations', conversationId);
    const convSnap = await getDoc(convRef);
    const convData = convSnap.exists() ? (convSnap.data() as any) : null;
    const unreadUpdates = buildUnreadIncrementsForSend(
      convData,
      currentUser.id,
    );

    // Update conversation's lastMessage + per-participant unread.
    // `lastMessageTime` is a duplicate of `lastMessageAt` for the
    // mobile reader (see mobile schema).
    await updateDoc(convRef, {
      lastMessage: content,
      lastMessageAt: serverTimestamp(),
      lastMessageTime: serverTimestamp(),
      lastMessageBy: currentUser.id,
      ...unreadUpdates,
    });

    // Trigger push notification in background (don't await)
    brain.send_chat_notification({
      conversationId,
      senderId: currentUser.id,
      senderName: currentUser.name,
      message: content,
      messageType: 'text'
    }).catch(err => console.error('Failed to send push notification:', err));
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

// Subscribe to conversations with participant details
export const subscribeToConversations = (
  userId: string,
  callback: (conversations: Conversation[]) => void,
  isAdmin: boolean = false
) => {
  const q = query(
    collection(db, 'conversations'),
    where('participants', 'array-contains', userId),
    orderBy('lastMessageAt', 'desc')
  );

  return onSnapshot(q, 
    async (snapshot) => {
      // If Firestore returns 0 conversations, fallback to backend API
      if (snapshot.docs.length === 0) {
        console.warn('⚠️ ChatService: No conversations from Firestore, trying backend API...');
        try {
          const response = await fetch(`${window.location.origin}/api/conversations`);
          const data = await response.json();
          if (data.data && data.data.length > 0) {
            // Filter conversations where user is a participant
            const userConversations = data.data.filter((conv: any) => 
              conv.participants && conv.participants.includes(userId)
            );
            
            // Transform backend data to Conversation format
            const conversations: Conversation[] = userConversations.map((conv: any) => ({
              id: conv.id,
              type: conv.type,
              name: conv.name,
              participants: conv.participants || [],
              clientId: conv.clientId,
              clientName: conv.clientName,
              otherUserName: undefined,
              otherUserAvatar: undefined,
              createdAt: conv.createdAt ? new Date(conv.createdAt) : new Date(),
              createdBy: conv.createdBy,
              lastMessage: conv.lastMessage,
              lastMessageAt: conv.lastMessageAt ? new Date(conv.lastMessageAt) : undefined,
              lastMessageBy: conv.lastMessageBy,
              lastReadBy: conv.lastReadBy || {},
              unreadCount: 0
            }));
            
            callback(conversations);
            return;
          }
        } catch (apiError) {
          console.error('❌ ChatService: Failed to fetch from backend API:', apiError);
        }
      }
      
      const conversations: Conversation[] = [];
      const clientIds = new Set<string>();
      
      // First pass: collect all conversations where user is a participant
      for (const docSnapshot of snapshot.docs) {
        const data = docSnapshot.data();
        
        // Skip deleted conversations
        if (data.deleted === true) {
          continue;
        }
        
        // Collect clientIds from admin_client conversations
        if (isAdmin && data.type === 'admin_client' && data.clientId) {
          clientIds.add(data.clientId);
        }
        
        // For one-on-one chats, get the other participant's details
        let otherUserName = undefined;
        let otherUserAvatar = undefined;
        
        if ((data.type === 'one_on_one' || data.type === 'admin_client') && data.participants?.length === 2) {
          const otherUserId = data.participants.find((id: string) => id !== userId);
          if (otherUserId) {
            // Create a cache key based on conversation ID and other user ID
            const cacheKey = `${docSnapshot.id}-${otherUserId}`;
            
            // Check if we have cached user details for this conversation
            const cachedDetails = conversationUserCache.get(cacheKey);
            if (cachedDetails) {
              otherUserName = cachedDetails.otherUserName;
              otherUserAvatar = cachedDetails.otherUserAvatar;
            } else {
              // Fetch user details and cache them
              const userDetails = await getUserDetailsFromStore(otherUserId);
              otherUserName = userDetails.name;
              otherUserAvatar = userDetails.photoURL;
              
              // Cache the details for this conversation
              conversationUserCache.set(cacheKey, {
                otherUserName,
                otherUserAvatar
              });
            }
          }
        }
      
      conversations.push({
        id: docSnapshot.id,
        type: data.type,
        name: data.name,
        participants: data.participants || [],
        clientId: data.clientId,
        clientName: data.clientName,
        otherUserName,
        otherUserAvatar,
        createdAt: convertTimestamp(data.createdAt),
        createdBy: data.createdBy,
        lastMessage: data.lastMessage,
        lastMessageAt: data.lastMessageAt ? convertTimestamp(data.lastMessageAt) : undefined,
        lastMessageBy: data.lastMessageBy,
        // Per-user unread counter — written by every send path
        // (see `buildUnreadIncrementsForSend`). Hydrating this lets
        // ConversationList / ChatSidebar render exact badge counts
        // instead of binary "has-unread".
        unreadCount: data.unreadCount || {},
        lastReadBy: data.lastReadBy ? Object.fromEntries(
          Object.entries(data.lastReadBy).map(([userId, timestamp]) => [
            userId,
            convertTimestamp(timestamp)
          ])
        ) : undefined
      });
    }
    
    // Second pass: if admin, fetch all other conversations with the same clientIds
    if (isAdmin && clientIds.size > 0) {
      try {
        const clientIdArray = Array.from(clientIds);
        
        dlog('Admin fetching additional conversations for clients:', clientIdArray);
        
        // Batch-fetch additional conversations via backend API in ONE
        // round-trip (previous version fired one request per client and
        // hit the platform's per-IP 429 rate limiter on admin logins
        // with 10+ clients).
        let byClient: Record<string, any[]> = {};
        try {
          const batchResp = await brain.get_client_conversations_batch({ client_ids: clientIdArray });
          const batchData = await batchResp.json();
          byClient = batchData?.by_client || {};
        } catch (batchErr) {
          console.error('[Chat Service] Batch client-conversations call failed:', batchErr);
          // Fall back to per-client fetch only if the batch endpoint failed
          // outright (network error, 500, etc).
          for (const clientId of clientIdArray) {
            try {
              const response = await brain.get_all_client_conversations({ clientId });
              const data = await response.json();
              byClient[clientId] = data.conversations || [];
            } catch (clientError) {
              console.error('[Chat Service] Error fetching conversations for client', clientId, ':', clientError);
            }
          }
        }

        for (const clientId of clientIdArray) {
          const convs = byClient[clientId] || [];
          const existingConversationIds = new Set(conversations.map((c) => c.id));
          dlog('Found', convs.length, 'conversations for client', clientId);
          for (const convData of convs) {
              // Skip if we already have this conversation
              if (existingConversationIds.has(convData.id)) {
                continue;
              }
              
              // Get other participant details
              let otherUserName = undefined;
              let otherUserAvatar = undefined;
              
              if (convData.participants?.length === 2) {
                // For these additional conversations, we want the client's details
                const clientUserId = convData.participants.find((id: string) => id !== userId);
                
                if (clientUserId) {
                  const cacheKey = `${convData.id}-${clientUserId}`;
                  const cachedDetails = conversationUserCache.get(cacheKey);
                  
                  if (cachedDetails) {
                    otherUserName = cachedDetails.otherUserName;
                    otherUserAvatar = cachedDetails.otherUserAvatar;
                  } else {
                    const userDetails = await getUserDetailsFromStore(clientUserId);
                    otherUserName = userDetails.name;
                    otherUserAvatar = userDetails.photoURL;
                    
                    conversationUserCache.set(cacheKey, {
                      otherUserName,
                      otherUserAvatar
                    });
                  }
                }
              }
              
              dlog('Adding additional conversation:', convData.id, 'for client:', convData.clientId);
              
              conversations.push({
                id: convData.id,
                type: convData.type,
                name: convData.name,
                participants: convData.participants || [],
                clientId: convData.clientId,
                clientName: convData.clientName,
                otherUserName,
                otherUserAvatar,
                createdAt: convData.createdAt ? new Date(convData.createdAt) : new Date(),
                createdBy: convData.createdBy,
                lastMessage: convData.lastMessage,
                lastMessageAt: convData.lastMessageAt ? new Date(convData.lastMessageAt) : undefined,
                lastMessageBy: convData.lastMessageBy,
                // Same per-user unread hydration as the primary
                // listener — keeps admin's "other clients" tab in
                // sync with the rest of the chat list.
                unreadCount: convData.unreadCount || {},
                lastReadBy: convData.lastReadBy ? Object.fromEntries(
                  Object.entries(convData.lastReadBy).map(([userId, timestamp]) => [
                    userId,
                    new Date(timestamp as string)
                  ])
                ) : undefined
              });
          }
        }
        
        // Sort all conversations by lastMessageAt
        conversations.sort((a, b) => {
          const timeA = a.lastMessageAt?.getTime() || 0;
          const timeB = b.lastMessageAt?.getTime() || 0;
          return timeB - timeA;
        });
      } catch (error) {
        console.error('[Chat Service] Error fetching additional conversations for admin:', error);
        // Continue with the conversations we already have
      }
    }
    
    callback(conversations);
  },
  (error) => {
    console.error('🚨 ChatService: Error subscribing to conversations:', error);
    console.error('🚨 Error code:', error.code);
    console.error('🚨 Error message:', error.message);
    // Return empty array on error
    callback([]);
  });
};

// ─── Message subscription pool ─────────────────────────────────────────
// Previously every `MessageThread` mount opened its own onSnapshot. When
// a chat sidebar + a popped-out chat window + a navigation re-mount all
// happened in quick succession we ended up with several listeners on the
// same `conversations/{id}/messages` subcollection. We now coalesce
// these into a per-conversation pool: one Firestore listener regardless
// of how many React components are watching, and a refcount so the
// listener tears down when the last consumer unmounts.
type MessagesCallback = (messages: Message[]) => void;

interface MessagesPoolEntry {
  unsubscribe: () => void;
  callbacks: Set<MessagesCallback>;
  lastMessages: Message[];
}

const _messagesPool = new Map<string, MessagesPoolEntry>();

// Subscribe to messages for a conversation
export const subscribeToMessages = (
  conversationId: string,
  callback: MessagesCallback,
) => {
  dlog('[Chat] Subscribing to messages for conversation:', conversationId);

  // Fast path: someone is already listening to this conversation. Just
  // hook our callback into the existing entry and replay the cached
  // last message list so the new consumer doesn't see an empty state.
  const existing = _messagesPool.get(conversationId);
  if (existing) {
    existing.callbacks.add(callback);
    if (existing.lastMessages.length) {
      callback(existing.lastMessages);
    }
    return () => {
      existing.callbacks.delete(callback);
      if (existing.callbacks.size === 0) {
        existing.unsubscribe();
        _messagesPool.delete(conversationId);
        dlog('[Chat] Tore down message listener for', conversationId);
      }
    };
  }

  // No pool entry yet → open a real Firestore listener.
  const q = query(
    collection(db, 'conversations', conversationId, 'messages'),
    orderBy('createdAt', 'asc')
  );

  const entry: MessagesPoolEntry = {
    callbacks: new Set([callback]),
    lastMessages: [],
    // tmp no-op until onSnapshot returns below.
    unsubscribe: () => {},
  };
  _messagesPool.set(conversationId, entry);

  const realUnsub = onSnapshot(q, (snapshot) => {
    dlog('[Chat] Messages snapshot received, count:', snapshot.size);
    const messages: Message[] = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      
      // Convert readBy to array - handle both array and object formats
      let readBy: { userId: string; timestamp: Date }[] = [];
      if (data.readBy) {
        if (Array.isArray(data.readBy)) {
          // Already an array
          readBy = data.readBy.map((r: any) => {
            let ts: Date;
            try {
              ts = r.timestamp instanceof Timestamp ? r.timestamp.toDate() : new Date(r.timestamp);
              // Check if date is valid
              if (isNaN(ts.getTime())) {
                ts = new Date(); // Fallback to current date
              }
            } catch {
              ts = new Date(); // Fallback to current date
            }
            return {
              userId: r.userId,
              timestamp: ts
            };
          });
        } else if (typeof data.readBy === 'object') {
          // It's an object/map — the native shape is
          // { uid: { name, timestamp } }; very old writes were
          // { uid: <timestamp> }. Accept both.
          readBy = Object.entries(data.readBy).map(([userId, value]: [string, any]) => {
            const raw =
              value && typeof value === 'object' && 'timestamp' in value
                ? value.timestamp
                : value;
            let ts: Date;
            try {
              ts = raw instanceof Timestamp ? raw.toDate() : new Date(raw);
              // Check if date is valid
              if (isNaN(ts.getTime())) {
                ts = new Date(); // Fallback to current date
              }
            } catch {
              ts = new Date(); // Fallback to current date
            }
            return {
              userId,
              timestamp: ts
            };
          });
        }
      }
      
      messages.push({
        id: doc.id,
        conversationId: conversationId,
        senderId: data.senderId,
        senderName: data.senderName,
        senderPhotoURL: data.senderPhotoURL,
        // Native (RN) writes `text` instead of `content`; web writes
        // `content`. Coalesce so both clients display correctly here.
        content: data.content ?? data.text ?? '',
        // Native uses `timestamp`; web uses `createdAt`. Either is fine.
        createdAt: convertTimestamp(data.createdAt ?? data.timestamp),
        type: data.type || 'text',
        // Native uses `imageUrl` + `fileName`; web uses `attachmentUrl` +
        // `attachmentName`. Accept either so cross-platform messages
        // render their attachments inline.
        attachmentUrl: data.attachmentUrl ?? data.imageUrl ?? data.fileUrl,
        attachmentName: data.attachmentName ?? data.fileName,
        // Native media markers — needed so the renderer can tell a video
        // (`fileType: 'video'`) apart from a plain document, and show the
        // native-generated `thumbnailUrl` as the video poster.
        fileType: data.fileType,
        fileUrl: data.fileUrl,
        imageUrl: data.imageUrl,
        thumbnailUrl: data.thumbnailUrl,
        duration: data.duration,
        callStatus: data.callStatus,
        // Emergency-call markers (native after-hours emergency flow) —
        // drive the red bubble + location card in MessageThread.
        isEmergency: data.isEmergency === true,
        emergencyReason: data.emergencyReason,
        emergencyLocation: data.emergencyLocation,
        readBy: readBy,
        reactions: data.reactions || {},
        // Tolerant reply reader — native shape is {id, text, senderName,
        // type}; accept the short-lived web shape {messageId, snippet} too.
        replyTo: (() => {
          const r: any = data.replyTo;
          if (!r || !(r.id || r.messageId)) return null;
          return {
            id: r.id || r.messageId,
            text: r.text ?? r.snippet ?? '',
            senderName: r.senderName || '',
            type: r.type || 'text',
          };
        })()
      });
    });
    dlog('[Chat] Processed messages:', messages.length);
    entry.lastMessages = messages;
    // Fan out to every consumer hooked into this conversation.
    entry.callbacks.forEach((cb) => cb(messages));
  }, (error) => {
    console.error('[Chat] Error subscribing to messages:', error);
    // If it's an index error, log the URL to create the index
    if (error.message?.includes('index')) {
      console.error('[Chat] Firestore index required. Check Firebase Console.');
    }
    entry.callbacks.forEach((cb) => cb([]));
  });

  entry.unsubscribe = realUnsub;

  return () => {
    entry.callbacks.delete(callback);
    if (entry.callbacks.size === 0) {
      entry.unsubscribe();
      _messagesPool.delete(conversationId);
      dlog('[Chat] Tore down message listener for', conversationId);
    }
  };
};

// Helper function to get client name (placeholder)
const getClientName = async (clientId: string): Promise<string> => {
  try {
    // Use brain API to get client details
    const response = await brain.list_clients();
    const clientData = await response.json();
    const clientName = clientData.clients?.[clientId];
    return clientName || `Client ${clientId.slice(-4)}`;
  } catch (error) {
    console.error('Error getting client name:', error);
    return 'Unknown Client';
  }
};

// Get all admin/staff users for conversation creation
// All super-admins — native "Office Admin" direct chats include every one of
// them. Hidden accounts (hideFromClientView, e.g. the Short Notice test
// super-admin) are excluded: they must never be added to chats.
export const getSuperAdmins = async (): Promise<ChatUser[]> => {
  const snapshot = await getDocs(
    query(collection(db, 'users'), where('isSuperAdmin', '==', true))
  );
  const users: ChatUser[] = [];
  snapshot.forEach((d) => {
    const data = d.data();
    if (data.hideFromClientView === true) return;
    users.push({
      id: d.id,
      name: data.displayName || data.name || 'Unknown User',
      role: data.role || 'admin',
      photoURL: data.photoURL || undefined,
    });
  });
  return users;
};

export const getAdminStaffUsers = async (): Promise<ChatUser[]> => {
  try {
    const q = query(
      collection(db, 'users'),
      where('role', 'in', ['admin', 'staff', 'contractor'])
    );
    
    const snapshot = await getDocs(q);
    const users: ChatUser[] = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      // Respect the hideFromClientView flag (e.g. the External Providers
      // test-admin account must never appear in messaging lists).
      if (data.hideFromClientView === true) return;
      if (data.isActive === false) return;
      if (data.role === 'admin' || data.role === 'staff' || data.role === 'contractor') {
        users.push({
          id: doc.id,
          name: data.name || data.email || 'Unknown User',
          role: data.role,
          photoURL: data.photoURL
        });
      }
    });
    
    return users;
  } catch (error) {
    console.error('Error getting admin/staff users:', error);
    return [];
  }
};

// Get all clients for admin-client chat creation
export const getClientUsers = async (): Promise<ChatUser[]> => {
  try {
    const response = await brain.list_clients();
    const clientData = await response.json();
    const clients: ChatUser[] = [];
    
    if (clientData.clients) {
      Object.entries(clientData.clients).forEach(([id, name]) => {
        clients.push({
          id,
          name: name as string,
          role: 'client'
        });
      });
    }
    
    return clients;
  } catch (error) {
    console.error('Error getting client users:', error);
    return [];
  }
};

// Mark conversation as read
export const markConversationAsRead = async (
  conversationId: string,
  userId: string
): Promise<void> => {
  try {
    // Mirror native build 2.6.0 — admin_client conversations have
    // shared staff read state. When a staff member reads, clear the
    // unread badge for ALL non-client participants so the rest of
    // the team doesn't keep being prompted. Client reads only
    // affect the client's own counter.
    const convRef = doc(db, 'conversations', conversationId);
    const convSnap = await getDoc(convRef);
    const convData = convSnap.exists() ? (convSnap.data() as any) : null;
    const unreadClears = buildUnreadClearsForRead(convData, userId);

    await updateDoc(convRef, {
      [`lastReadBy.${userId}`]: serverTimestamp(),
      ...unreadClears,
    });
  } catch (error) {
    console.error('Error marking conversation as read:', error);
    throw error;
  }
};

// Send image message
export const sendImageMessage = async (
  conversationId: string,
  file: File,
  currentUser: ChatUser
): Promise<void> => {
  try {
    // Compress + resize the image. Native clients upload the raw file
    // straight to Firebase Storage; the web also uploads to Storage now
    // (so RN clients can render via the resulting download URL), but we
    // still resize down to 1280px / JPEG-0.8 so we're not pushing
    // 5-megabyte camera originals through the browser.
    const compressedBlob = await new Promise<Blob>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const MAX_EDGE = 1280;
          let { width, height } = img;
          if (width > height && width > MAX_EDGE) {
            height = Math.round(height * (MAX_EDGE / width));
            width = MAX_EDGE;
          } else if (height >= width && height > MAX_EDGE) {
            width = Math.round(width * (MAX_EDGE / height));
            height = MAX_EDGE;
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas context unavailable'));
            return;
          }
          ctx.drawImage(img, 0, 0, width, height);
          const isLikelyPhoto = file.type === 'image/jpeg' || file.type === 'image/jpg' || width * height > 250000;
          const mime = isLikelyPhoto ? 'image/jpeg' : (file.type || 'image/jpeg');
          canvas.toBlob(
            (blob) => {
              if (blob) resolve(blob);
              else reject(new Error('Failed to encode compressed image'));
            },
            mime,
            0.8,
          );
        };
        img.onerror = reject;
        img.src = reader.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    // Mirror the native app's path so both clients can read each other's
    // images out of the same bucket folder:
    //   chat-attachments/{conversationId}/{epochMillis}.{ext}
    const ext = (() => {
      const fromType = (file.type || compressedBlob.type || 'image/jpeg').split('/')[1];
      return (fromType || 'jpg').replace('jpeg', 'jpg');
    })();
    const objectPath = `chat-attachments/${conversationId}/${Date.now()}.${ext}`;
    const ref = storageRef(storage, objectPath);
    const uploadResult = await uploadBytes(ref, compressedBlob, {
      contentType: compressedBlob.type || file.type || 'image/jpeg',
    });
    const downloadUrl = await getDownloadURL(uploadResult.ref);

    // Write message — dual-schema so both web AND native render it.
    // Native (RN) reads: `imageUrl`, `fileName`, `fileType`, `text`,
    //                     `timestamp`, `type === 'image'`, `read`.
    // Web reads:        `attachmentUrl`, `attachmentName`, `content`,
    //                    `createdAt`, `type === 'image'`.
    // Writing every field once keeps both clients happy without forcing
    // a migration of older docs.
    const messageData = {
      senderId: currentUser.id,
      senderName: currentUser.name,
      senderPhotoURL: currentUser.photoURL || null,
      type: 'image',
      // Native fields
      imageUrl: downloadUrl,
      fileName: file.name,
      fileType: 'image',
      text: '',
      timestamp: serverTimestamp(),
      read: false,
      // Web fields (kept for backwards compatibility with existing
      // MessageThread.tsx rendering).
      content: '',
      attachmentUrl: downloadUrl,
      attachmentName: file.name,
      attachmentPath: objectPath,
      createdAt: serverTimestamp(),
    };

    await addDoc(collection(db, 'conversations', conversationId, 'messages'), messageData);

    // Mirror native build 2.6.0 — per-participant unread bookkeeping.
    // See `buildUnreadIncrementsForSend` for the contract.
    const imgConvRef = doc(db, 'conversations', conversationId);
    const imgConvSnap = await getDoc(imgConvRef);
    const imgConvData = imgConvSnap.exists() ? (imgConvSnap.data() as any) : null;
    const imgUnreadUpdates = buildUnreadIncrementsForSend(
      imgConvData,
      currentUser.id,
    );

    // Update conversation's lastMessage + per-participant unread.
    // `lastMessageTime` mirrors `lastMessageAt` for mobile.
    await updateDoc(imgConvRef, {
      lastMessage: '📷 Image',
      lastMessageAt: serverTimestamp(),
      lastMessageTime: serverTimestamp(),
      lastMessageBy: currentUser.id,
      ...imgUnreadUpdates,
    });

    // Trigger push notification in background (don't await)
    brain.send_chat_notification({
      conversationId,
      senderId: currentUser.id,
      senderName: currentUser.name,
      message: '📷 Image',
      messageType: 'image'
    }).catch(err => console.error('Failed to send push notification:', err));
  } catch (error) {
    console.error('Error sending image:', error);
    throw error;
  }
};

// Send file message
export const sendFileMessage = async (
  conversationId: string,
  file: File,
  currentUser: ChatUser
): Promise<void> => {
  try {
    // Convert file to base64 for storage
    const reader = new FileReader();
    const base64 = await new Promise<string>((resolve, reject) => {
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    // Mobile schema differentiates video from generic files via
    // `fileType: 'video'`. Detect from MIME.
    const mime = file.type || '';
    const inferredFileType =
      mime.startsWith('video/') ? 'video' :
      mime.startsWith('audio/') ? 'audio' :
      mime.startsWith('image/') ? 'image' :
      'document';

    // Write message directly to Firestore. Dual-schema so both web
    // and native render it (`content`/`text`, `createdAt`/`timestamp`,
    // `attachmentUrl`/`fileUrl`).
    const messageData = {
      senderId: currentUser.id,
      senderName: currentUser.name,
      senderPhotoURL: currentUser.photoURL || null,
      content: '',
      text: '',
      createdAt: serverTimestamp(),
      timestamp: serverTimestamp(),
      type: 'file',
      // Native fields
      fileUrl: base64,
      fileName: file.name,
      fileType: inferredFileType,
      read: false,
      // Web fields (legacy dup)
      attachmentUrl: base64,
      attachmentName: file.name,
    };

    await addDoc(collection(db, 'conversations', conversationId, 'messages'), messageData);

    // Mirror native build 2.6.0 — per-participant unread bookkeeping.
    const fileConvRef = doc(db, 'conversations', conversationId);
    const fileConvSnap = await getDoc(fileConvRef);
    const fileConvData = fileConvSnap.exists() ? (fileConvSnap.data() as any) : null;
    const fileUnreadUpdates = buildUnreadIncrementsForSend(
      fileConvData,
      currentUser.id,
    );

    // Update conversation's lastMessage + per-participant unread.
    // `lastMessageTime` mirrors `lastMessageAt` for mobile. The
    // lastMessage preview matches the mobile contract: '📹 Video' for
    // videos, '📎 <filename>' for everything else.
    const previewLabel = inferredFileType === 'video' ? '📹 Video' : `📎 ${file.name}`;
    await updateDoc(fileConvRef, {
      lastMessage: previewLabel,
      lastMessageAt: serverTimestamp(),
      lastMessageTime: serverTimestamp(),
      lastMessageBy: currentUser.id,
      ...fileUnreadUpdates,
    });

    // Trigger push notification in background (don't await)
    brain.send_chat_notification({
      conversationId,
      senderId: currentUser.id,
      senderName: currentUser.name,
      message: previewLabel,
      messageType: 'file',
    }).catch(err => console.error('Failed to send push notification:', err));
  } catch (error) {
    console.error('Error sending file:', error);
    throw error;
  }
};

// Mark messages as read - updates individual messages and conversation's lastReadMessageId
export const markMessagesAsRead = async (
  conversationId: string,
  userId: string,
  lastVisibleMessageId?: string // The last message currently visible/scrolled to
): Promise<void> => {
  try {
    // Messages are in a subcollection: conversations/{conversationId}/messages
    const messagesRef = collection(db, 'conversations', conversationId, 'messages');
    const conversationRef = doc(db, 'conversations', conversationId);
    
    dlog(`🔍 Fetching all messages in conversation ${conversationId}`);
    
    // Get all messages in the conversation (no where clause needed since we're in the subcollection)
    const snapshot = await getDocs(messagesRef);
    dlog(`📬 Found ${snapshot.size} total messages in conversation`);
    
    const batch = writeBatch(db);
    let updateCount = 0;
    let lastMessageIdToMark = lastVisibleMessageId;

    // Load the conversation once up front — participantNames feeds the
    // native readBy map shape ({ uid: { name, timestamp } }) and the
    // same doc drives the unread-state clears at the bottom.
    const convSnap = await getDoc(conversationRef);
    const convData = convSnap.exists() ? (convSnap.data() as any) : null;
    const readerName =
      convData?.participantNames?.[userId] ||
      firebaseAuth.currentUser?.displayName ||
      '';
    const readerPhoto = firebaseAuth.currentUser?.photoURL || null;
    
    // Sort messages by createdAt manually and filter out current user's messages
    const sortedDocs = snapshot.docs
      .filter(doc => doc.data().senderId !== userId) // Only messages from others
      .sort((a, b) => {
        const aTime = a.data().createdAt?.toMillis() || 0;
        const bTime = b.data().createdAt?.toMillis() || 0;
        return aTime - bTime;
      });
    
    dlog(`📬 Found ${sortedDocs.length} messages from other users`);
    
    // If no specific message provided, mark all messages
    if (!lastVisibleMessageId && sortedDocs.length > 0) {
      lastMessageIdToMark = sortedDocs[sortedDocs.length - 1].id;
    }
    
    // Find the index of the last message to mark
    let lastMessageIndex = -1;
    if (lastMessageIdToMark) {
      lastMessageIndex = sortedDocs.findIndex(d => d.id === lastMessageIdToMark);
    }
    
    // Mark all messages up to and including the last visible message
    sortedDocs.forEach((docSnapshot, index) => {
      // If we have a specific last message, only mark up to that point
      if (lastMessageIndex >= 0 && index > lastMessageIndex) {
        return; // Skip messages after the last visible one
      }
      
      const data = docSnapshot.data();
      const rawReadBy = data.readBy || [];
      
      // Handle both array and object formats for readBy
      let alreadyRead = false;
      if (Array.isArray(rawReadBy)) {
        alreadyRead = rawReadBy.some((r: any) => r && r.userId === userId);
      } else if (typeof rawReadBy === 'object') {
        // It's an object/map like {userId: timestamp}
        alreadyRead = userId in rawReadBy;
      }
      
      // Only update if not already read by this user
      if (!alreadyRead) {
        dlog(`📝 Marking message ${docSnapshot.id} as read by ${userId}`);
        
        // Native app shape: readBy is a MAP { uid: { name, timestamp } }.
        // The old web write (arrayUnion of { userId, timestamp }) made the
        // native client's Object.entries() treat array indices "0"/"1" as
        // fake user ids → receipts rendered as "User"/"US". Write the map
        // shape; if this message still carries a legacy array (or []),
        // convert it wholesale so both clients agree.
        if (Array.isArray(rawReadBy)) {
          const migrated: Record<string, any> = {};
          rawReadBy.forEach((r: any) => {
            if (r && r.userId) {
              migrated[r.userId] = {
                name: convData?.participantNames?.[r.userId] || '',
                photoURL: null,
                timestamp: r.timestamp || Timestamp.now(),
              };
            }
          });
          migrated[userId] = { name: readerName, photoURL: readerPhoto, timestamp: Timestamp.now() };
          batch.update(docSnapshot.ref, { readBy: migrated });
        } else {
          batch.update(docSnapshot.ref, {
            [`readBy.${userId}`]: { name: readerName, photoURL: readerPhoto, timestamp: Timestamp.now() },
          });
        }
        updateCount++;
      } else {
        dlog(`✓ Message ${docSnapshot.id} already read by ${userId}`);
      }
    });
    
    // Update conversation with last read message ID + shared
    // unread-state clears (admin_client staff readers clear the
    // badge for the whole team — see `buildUnreadClearsForRead`).
    if (lastMessageIdToMark) {
      // convData was loaded up front (see above) — reuse it for the
      // shared-vs-private unread clears.
      const unreadClears = buildUnreadClearsForRead(convData, userId);

      batch.update(conversationRef, {
        [`lastReadMessageId.${userId}`]: lastMessageIdToMark,
        [`lastReadBy.${userId}`]: serverTimestamp(),
        ...unreadClears,
      });
    }
    
    if (updateCount > 0 || lastMessageIdToMark) {
      try {
        await batch.commit();
        dlog(`✅ Successfully marked ${updateCount} messages as read up to message ${lastMessageIdToMark}`);
      } catch (commitError: any) {
        console.error('❌ Error committing batch update:', commitError);
        console.error('❌ Error code:', commitError.code);
        console.error('❌ Error message:', commitError.message);
        throw commitError;
      }
    } else {
      dlog(`ℹ️ All messages already marked as read in conversation ${conversationId}`);
    }
  } catch (error: any) {
    console.error('❌ Error marking messages as read:', error);
    console.error('❌ Error code:', error?.code);
    console.error('❌ Error message:', error?.message);
    throw error;
  }
};

// Delete conversation (admin only)
export const deleteConversation = async (
  conversationId: string
): Promise<void> => {
  try {
    // First refresh token to ensure we have latest custom claims
    const user = firebaseAuth.currentUser;
    if (!user) {
      throw new Error('No authenticated user found');
    }
    
    await user.getIdToken(true); // Force refresh to get updated claims
    
    // Check if user has admin or staff role
    const idTokenResult = await user.getIdTokenResult();
    const claims = idTokenResult?.claims || {};
    
    const isAdmin = claims.role === 'admin' || claims.admin === true;
    const isStaff = claims.role === 'staff' || claims.staff === true;
    
    if (!isAdmin && !isStaff) {
      throw new Error('Insufficient permissions. Admin or staff role required to delete conversations.');
    }
    
    // First, delete all messages in the conversation subcollection
    // Messages are stored as: conversations/{conversationId}/messages/{messageId}
    const messagesRef = collection(db, 'conversations', conversationId, 'messages');
    const messagesSnapshot = await getDocs(messagesRef);
    
    dlog(`[Chat] Deleting ${messagesSnapshot.size} messages from conversation ${conversationId}`);
    
    const deletePromises = messagesSnapshot.docs.map(messageDoc => {
      return deleteDoc(messageDoc.ref);
    });
    
    await Promise.all(deletePromises);
    
    // Then mark the conversation as deleted (soft delete)
    await updateDoc(doc(db, 'conversations', conversationId), {
      deleted: true,
      deletedAt: new Date(),
      deletedBy: user.uid
    });
    
    dlog(`[Chat] Conversation ${conversationId} deleted successfully`);
  } catch (error) {
    console.error('[Chat] Error deleting conversation:', error);
    throw error;
  }
};

// Edit message (sender only)
export const editMessage = async (
  conversationId: string,
  messageId: string,
  newContent: string
): Promise<void> => {
  try {
    const user = firebaseAuth.currentUser;
    if (!user) {
      throw new Error('No authenticated user found');
    }

    dlog('Edit message service called:', {
      conversationId,
      messageId,
      userId: user.uid,
      newContent
    });

    // Messages are stored as subcollection: conversations/{conversationId}/messages/{messageId}
    const messageRef = doc(db, 'conversations', conversationId, 'messages', messageId);
    
    dlog('Attempting to update message in Firestore subcollection...');
    // Update the message - Firestore rules will verify sender.
    // Write both `content` and `text` (mobile reads `content || text`).
    await updateDoc(messageRef, {
      content: newContent,
      text: newContent,
      edited: true,
      editedAt: serverTimestamp()
    });
    dlog('Message updated successfully');

    // Update last message in conversation if this was the last message
    // We need to read the conversation, but as a participant we should have read access
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationSnapshot = await getDoc(conversationRef);

      if (conversationSnapshot.exists()) {
        const conversationData = conversationSnapshot.data();
        if (conversationData.lastMessageId === messageId) {
          await updateDoc(conversationRef, {
            lastMessage: newContent
          });
          dlog('Conversation last message updated');
        }
      }
    } catch (convError) {
      // Non-critical - conversation update failed but message was edited
      console.warn('Could not update conversation last message:', convError);
    }
  } catch (error: any) {
    console.error('Error editing message:', error);
    console.error('Error code:', error?.code);
    console.error('Error message:', error?.message);
    
    // Provide user-friendly error message
    if (error?.code === 'permission-denied') {
      throw new Error('You can only edit your own messages');
    }
    throw error;
  }
};

// Delete message (sender only)
export const deleteMessage = async (
  conversationId: string,
  messageId: string
): Promise<void> => {
  try {
    const user = firebaseAuth.currentUser;
    if (!user) {
      throw new Error('No authenticated user found');
    }

    dlog('Delete message service called:', {
      conversationId,
      messageId,
      userId: user.uid
    });

    // Messages are stored as subcollection: conversations/{conversationId}/messages/{messageId}
    // Note: Firestore rules only allow admin to delete, not regular users
    // So we do a soft delete via update instead
    
    const messageRef = doc(db, 'conversations', conversationId, 'messages', messageId);
    
    dlog('Attempting to soft delete (update) message in subcollection...');
    // Soft delete the message via update - rules allow sender to update.
    // Native rule: never hard-delete (use deleteDoc). Write content +
    // text mirror so both clients show the tombstone.
    await updateDoc(messageRef, {
      deleted: true,
      deletedAt: serverTimestamp(),
      content: 'This message was deleted',
      text: 'This message was deleted',
    });
    dlog('Message soft deleted successfully');

    // Update last message in conversation if this was the last message
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationSnapshot = await getDoc(conversationRef);

      if (conversationSnapshot.exists()) {
        const conversationData = conversationSnapshot.data();
        if (conversationData.lastMessageId === messageId) {
          // Find the previous message to update the conversation
          const previousMessagesQuery = query(
            collection(db, 'conversations', conversationId, 'messages'),
            where('deleted', '!=', true),
            orderBy('createdAt', 'desc')
          );
          const previousMessages = await getDocs(previousMessagesQuery);
          
          if (!previousMessages.empty) {
            const lastMessage = previousMessages.docs[0].data();
            await updateDoc(conversationRef, {
              lastMessage: lastMessage.content,
              lastMessageAt: lastMessage.createdAt,
              lastMessageTime: lastMessage.createdAt,
              lastMessageId: previousMessages.docs[0].id,
              lastMessageBy: lastMessage.senderId
            });
          } else {
            // No more messages, update to show conversation created
            await updateDoc(conversationRef, {
              lastMessage: 'No messages yet',
              lastMessageAt: conversationData.createdAt,
              lastMessageTime: conversationData.createdAt,
            });
          }
        }
      }
    } catch (convError) {
      // Non-critical - conversation update failed but message was deleted
      console.warn('Could not update conversation last message:', convError);
    }
  } catch (error: any) {
    console.error('Error deleting message:', error);
    console.error('Error code:', error?.code);
    
    // Provide user-friendly error message
    if (error?.code === 'permission-denied') {
      throw new Error('You can only delete your own messages');
    }
    throw error;
  }
};

// Toggle reaction on a message
export const toggleReaction = async (
  conversationId: string,
  messageId: string,
  userId: string,
  emoji: string
): Promise<void> => {
  try {
    // Access message as subcollection of conversation
    const messageRef = doc(db, 'conversations', conversationId, 'messages', messageId);
    const messageDoc = await getDoc(messageRef);
    
    if (!messageDoc.exists()) {
      throw new Error('Message not found');
    }
    
    const messageData = messageDoc.data();
    const reactions = messageData.reactions || {};
    
    // If user already reacted with this emoji, remove it. Otherwise, add/update it.
    if (reactions[userId] === emoji) {
      // Remove reaction
      delete reactions[userId];
    } else {
      // Add/update reaction
      reactions[userId] = emoji;
    }
    
    await updateDoc(messageRef, {
      reactions
    });
  } catch (error: any) {
    console.error('Error toggling reaction:', error);
    throw error;
  }
};

